import React from 'react'
import styled from '@emotion/styled'
import { useSearchParams } from 'react-router'
import { useDisableAppHeaderSticky } from '@core/Areas/AppHeader/hooks/useDisableAppHeaderSticky'
import { MainContainer } from '@core/Components/Containers'
import { PageHeader } from '@mobi/component-library/Navigation/PageHeader'
import { debounce } from '@mobi/utils/functions'
import { Icon, Input } from '@mobi/component-library/Common/V2'
import { colors, font, shadow, spacing } from '@mobi/component-library/Theme/Common'
import { useExploreSuggestedProfiles } from '@core/Areas/BetLoop/hooks/useExploreSuggestedProfiles/useExploreSuggestedProfiles'
import { ProfilesList } from '@core/Areas/BetLoop/Components/Common/ProfilesList'
import { useSearchAllProfiles } from '@core/Areas/BetLoop/hooks/useSearchAllProfiles/useSearchAllProfiles'

const enum LocalConstants {
  SearchProfilesNoResultsClass = 'js-search-profiles-no-results_label',
  SearchProfilesSuggestedLabel = 'js-search-profiles-suggested_label',
}

export const SearchProfilesPage = () => {
  useDisableAppHeaderSticky()

  const [searchQuery, setSearchQuery] = useSearchProfilesParamState('q', '')
  const [invalidSearch, setInvalidSearch] = React.useState<boolean>(false)

  const searchInputRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    const el = searchInputRef.current
    if (!el) return
    const handleScroll = () => (el.style.boxShadow = el.offsetTop > 2 ? shadow.md : 'none')
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [searchInputRef])

  const {
    data: suggestedData,
    LoadMoreButton,
    isLoading,
    refetch,
  } = useExploreSuggestedProfiles()

  const {
    data: searchData,
    LoadMoreButton: searchLoadMoreButton,
    isEmptyState: searchIsEmptyState,
    isLoading: searchIsLoading,
    refetch: searchRefetch,
  } = useSearchAllProfiles(searchQuery)

  const handleChangeSearchQuery = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    if (/[^a-zA-Z0-9\s.,'"-]/.test(value)) setInvalidSearch(true)
    setSearchQuery(value, { shouldReplace: true })
    if (value.length > 1) {
      debounce(() => searchRefetch(), 1000)()
      if (invalidSearch) setInvalidSearch(false)
    }
    if (value.length < 2) {
      refetch()
      if (invalidSearch) setInvalidSearch(false)
    }
  }

  const handleClearSearch = () => {
    setSearchQuery('', { shouldReplace: true })
    setInvalidSearch(false)
  }

  const shouldShowSuggestedProfiles = searchQuery.length < 2 || searchIsEmptyState || invalidSearch
  const shouldShowSearchProfiles = searchQuery.length >= 2

  return (
    <MainContainer background='surface300'>
      <PageHeader title='Find Profiles' />

      <SearchProfilesWrapperStyled>
        <div ref={searchInputRef}>
          <Input
            prefix={<Icon name='LineSearchSm' size='2rem' color={colors.neutral[700]} />}
            suffix={
              searchQuery && searchQuery.toString().length > 0 ? (
                <Icon onClick={handleClearSearch} name='SolidXClose' />
              ) : null
            }
            name='Search'
            type='text'
            placeholder='Search for profiles'
            value={searchQuery}
            onChange={handleChangeSearchQuery}
            data-testid='clear-search-icon'
          />
        </div>

        {shouldShowSuggestedProfiles && (
          <>
            {searchIsEmptyState && (
              <div className={LocalConstants.SearchProfilesNoResultsClass}>
                <span>No results found for "{searchQuery}"</span>
              </div>
            )}

            <div className={LocalConstants.SearchProfilesSuggestedLabel}>
              <span>Suggested for you</span>
            </div>

            <ProfilesList
              data={suggestedData}
              isLoading={isLoading}
              LoadMoreButton={LoadMoreButton}
              searchQuery={searchQuery}
              location='search-page'
            />
          </>
        )}

        {shouldShowSearchProfiles && (
          <ProfilesList
            data={searchData}
            isLoading={searchIsLoading}
            LoadMoreButton={searchLoadMoreButton}
            searchQuery={searchQuery}
            location='search-page'
            shouldShowSkeletonLoader={false}
          />
        )}
      </SearchProfilesWrapperStyled>
    </MainContainer>
  )
}

// =======
// Helpers
// =======

const useSearchProfilesParamState = (key: string, defaultValue = '') => {
  const [searchParams, setSearchParams] = useSearchParams()

  const currentValue = searchParams.get(key) ?? defaultValue

  const setValue = React.useCallback(
    (newValue: string, { shouldReplace = false }: { shouldReplace?: boolean } = {}) => {
      setSearchParams(
        prev => {
          const params = new URLSearchParams(prev)
          if (!newValue) params.delete(key)
          else params.set(key, newValue)
          return params
        },
        { replace: shouldReplace }
      )
    },
    [key, setSearchParams]
  )

  return [currentValue, setValue] as const
}

// ======
// Styles
// ======

const SearchProfilesWrapperStyled = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'stretch',
  position: 'relative',

  '> div:first-of-type': {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    padding: `${spacing.sm} ${spacing.md}`,
    background: colors.white,
    transition: 'box-shadow 0.125s ease',
    gap: '1rem',
  },

  '> div': {
    [`&.${LocalConstants.SearchProfilesSuggestedLabel}`]: {
      display: 'flex',
      gap: spacing.md,
      alignSelf: 'stretch',
      padding: `${spacing.smx1} ${spacing.md}`,
      background: colors.white,

      '> span': {
        color: colors.lavender[900],
        fontFamily: font.family.primary,
        fontSize: font.size.lg.fontSize,
        fontWeight: font.weight.medium,
        lineHeight: font.size.lg.lineHeight,
      },
    },

    [`&.${LocalConstants.SearchProfilesNoResultsClass}`]: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: spacing.bigx1,

      '> span': {
        fontFamily: font.family.primary,
        fontSize: font.size.lg.fontSize,
        fontWeight: font.weight.regular,
        lineHeight: font.size.lg.lineHeight,
      },
    },
  },
})
